import { isNullOrUndefinedOrEmpty } from "../filter-value";
import { isValidEmail } from "../email-validations";
import { toWizardDate } from "../current-loan-encompass-mappers";

// Mirrors the Add New Loan Step 1 rules in src/utils/requiredFields.js (requiredFields +
// requiredFieldsForAdditionalContact), applied to the Dashboard's state shape.

const REQUIRED_MESSAGE =
	"Please provide a value for all fields highlighted above.";

const hasAnyPhone = (person) =>
	!isNullOrUndefinedOrEmpty(person.cellPhone) ||
	!isNullOrUndefinedOrEmpty(person.workPhone) ||
	!isNullOrUndefinedOrEmpty(person.homePhone);

const isZero = (value) => Number(value) === 0;

// A date of birth the save can convert (toWizardDate → MM-DD-YYYY), that is a real calendar
// date and not in the future. Anything else would reach Encompass as raw text.
const isValidDob = (value) => {
	const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(toWizardDate(String(value || "").trim()));
	if (!match) return false;
	const [month, day, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
	const date = new Date(Date.UTC(year, month - 1, day));
	return date.getUTCMonth() === month - 1 && date.getUTCDate() === day && date <= new Date();
};

const collectPersonErrors = (person, prefix, errors) => {
	if (isNullOrUndefinedOrEmpty(person.firstName)) errors.add(`${prefix}.firstName`);
	if (isNullOrUndefinedOrEmpty(person.lastName)) errors.add(`${prefix}.lastName`);
	if (isNullOrUndefinedOrEmpty(person.dob) || !isValidDob(person.dob)) errors.add(`${prefix}.dob`);
	if (isNullOrUndefinedOrEmpty(person.ssn)) errors.add(`${prefix}.ssn`);
	if (isNullOrUndefinedOrEmpty(person.maritalStatus)) errors.add(`${prefix}.maritalStatus`);
	if (isNullOrUndefinedOrEmpty(person.citizenship)) errors.add(`${prefix}.citizenship`);

	if (
		(person.citizenship === "NonPermanentResidentAlien" ||
			person.citizenship === "Foreign National") &&
		isNullOrUndefinedOrEmpty(person.countryOfCitizenship)
	) {
		errors.add(`${prefix}.countryOfCitizenship`);
	}

	if (!hasAnyPhone(person)) errors.add(`${prefix}.cellPhone`);

	// Required, as in the wizard; the format check is added per CLAUDE.md (call
	// isValidEmail for every field that collects an email address).
	if (isNullOrUndefinedOrEmpty(person.email) || !isValidEmail(person.email)) {
		errors.add(`${prefix}.email`);
	}
};

// Subject Property Details card (Step 1): address, units, gross rent, insurance, taxes.
const collectPropertyErrors = (property, errors) => {
	const pr = property || {};
	if (isNullOrUndefinedOrEmpty(pr.street)) errors.add("property.street");
	if (isNullOrUndefinedOrEmpty(pr.city)) errors.add("property.city");
	if (isNullOrUndefinedOrEmpty(pr.state)) errors.add("property.state");
	if (isNullOrUndefinedOrEmpty(pr.zip)) errors.add("property.zip");
	if (isNullOrUndefinedOrEmpty(pr.units) || isZero(pr.units)) errors.add("property.units");
	// gross rent may be 0, but must be entered
	if (isNullOrUndefinedOrEmpty(pr.grossRent)) errors.add("property.grossRent");
	if (isNullOrUndefinedOrEmpty(pr.homeownersInsurance) || isZero(pr.homeownersInsurance)) {
		errors.add("property.homeownersInsurance");
	}
	if (isNullOrUndefinedOrEmpty(pr.propertyTaxes) || isZero(pr.propertyTaxes)) {
		errors.add("property.propertyTaxes");
	}
};

// pair = { borrower, coBorrower, property, additionalContact, loanOriginatorId, loanProcessorId,
//          isNdcOrCorr }
export const getDashboardBorrowerFieldErrors = (pair) => {
	const errors = new Set();
	const {
		borrower,
		coBorrower,
		property,
		additionalContact,
		loanOriginatorId,
		loanProcessorId,
		isNdcOrCorr,
	} = pair || {};

	collectPersonErrors(borrower || {}, "borrower", errors);

	// Co-borrower is only validated once a first name is entered (requiredFields.js:124-190).
	const hasCoBorrower = !isNullOrUndefinedOrEmpty(coBorrower?.firstName);
	if (hasCoBorrower) collectPersonErrors(coBorrower, "coBorrower", errors);

	collectPropertyErrors(property, errors);

	if (isNullOrUndefinedOrEmpty(loanOriginatorId)) errors.add("loanOriginatorId");
	if (isNullOrUndefinedOrEmpty(loanProcessorId)) errors.add("loanProcessorId");

	// requiredFieldsForAdditionalContact: a name makes phone and email required. The wizard
	// only shows and checks the additional contact for NDC / CORR (edit-loan-application:939-950).
	if (isNdcOrCorr && additionalContact && !isNullOrUndefinedOrEmpty(additionalContact.name)) {
		if (isNullOrUndefinedOrEmpty(additionalContact.phone)) errors.add("additionalContact.phone");
		if (
			isNullOrUndefinedOrEmpty(additionalContact.email) ||
			!isValidEmail(additionalContact.email)
		) {
			errors.add("additionalContact.email");
		}
	}

	return errors;
};

export const validateDashboardBorrower = (pair, setError) => {
	const errors = getDashboardBorrowerFieldErrors(pair);
	if (errors.size > 0) {
		setError(REQUIRED_MESSAGE);
		return false;
	}
	return true;
};
